import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Check, X } from "lucide-react";
import { DetailPageSkeleton } from "@/components/skeletons/PageSkeletons";

function pctColor(pct) {
  if (pct >= 90) return "text-[#059669]";
  if (pct >= 75) return "text-[#C8102E]";
  if (pct >= 60) return "text-[#D97706]";
  return "text-[#E11D48]";
}

const statusChip = {
  present: { cls: "bg-[#D1FAE5] text-[#059669]", label: "Present", icon: Check },
  absent:  { cls: "bg-[#FFE4E6] text-[#E11D48]", label: "Absent", icon: X },
};

export default function StudentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get(`/students/${id}`).then(r => setData(r.data)); }, [id]);
  if (!data) return <DetailPageSkeleton testId="student-loading" statCount={2} />;

  const absentCount = data.records.filter(r => r.status === "absent").length;

  return (
    <div className="min-h-screen">
      <div className="px-6 md:px-12 pt-10 pb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#0F172A] mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[#FDECEF] text-[#C8102E] flex items-center justify-center font-bold text-lg">
            {data.student.name.split(" ").map(x => x[0]).slice(0,2).join("")}
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">Roll {data.student.roll_no} · Section {data.student.section_name}</div>
            <h1 className="font-heading text-4xl font-extrabold tracking-tighter text-[#0F172A] mt-1" data-testid="student-heading">{data.student.name}</h1>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 border-t border-l border-[#E2E8F0] bg-white">
        <div className="border-r border-b border-[#E2E8F0] p-6" data-testid="stat-attendance">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">Attendance</div>
          <div className={`font-heading text-3xl font-extrabold tracking-tighter mt-2 ${pctColor(data.attendance_pct)}`}>{data.attendance_pct}%</div>
          <div className="text-xs text-[#475569] mt-1">{data.present}/{data.total} sessions</div>
        </div>
        <div className="border-r border-b border-[#E2E8F0] p-6" data-testid="stat-absent">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">Absent</div>
          <div className="font-heading text-3xl font-extrabold tracking-tighter text-[#E11D48] mt-2">{absentCount}</div>
        </div>
      </div>

      <div className="px-6 md:px-12 py-10">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A] mb-6">History</h2>
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="history-list">
          {data.records.length === 0 ? (
            <div className="px-6 py-8 text-center text-[#94A3B8] text-sm">No attendance records yet.</div>
          ) : data.records.map((r, i) => {
            const chip = statusChip[r.status === "late" ? "present" : r.status] || statusChip.absent;
            const Icon = chip.icon;
            return (
              <div key={r.id || i} className={`px-6 py-3 flex items-center gap-4 ${i > 0 ? "border-t border-[#E2E8F0]" : ""}`} data-testid={`record-${i}`}>
                <div className="w-24 text-sm font-semibold text-[#0F172A]">{r.date}</div>
                <div className="flex-1 min-w-0 text-sm text-[#475569] truncate">{r.course_code} · {r.course_name}</div>
                <div className="text-xs text-[#94A3B8]">{r.source || "manual"}</div>
                <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-semibold ${chip.cls}`}>
                  <Icon className="w-3.5 h-3.5" />
                  {chip.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
